import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PROMO_CODE_VERIFICATION_STATUS } from '../../features/promotions/types/promotionTypes';
import { usePromotionsContext } from '../../hooks/usePromotionsContext';
import promotionCoupon from '../../images/promotion-coupon.svg';

const PromoCodeWidget = () => {
  const [promoCode, setPromoCode] = useState<string>('');
  const [showInputWidget, setInputWidget] = useState<boolean>(true);
  const [disabledPromoCodes, setDisabledPromoCodes] = useState<boolean>(false);

  const promoCodeInputRef = useRef<HTMLInputElement>(null);
  const {
    promoCodeWidgetData,
    onPromoCodeApply,
    resetPromocode,
    availablePromoCodes,
    appliedPromotionsData,
  } = usePromotionsContext();
  const intl = useIntl();

  const { promoVerificationStatus, appliedPromoCode } = promoCodeWidgetData;
  const isLoading =
    promoVerificationStatus === PROMO_CODE_VERIFICATION_STATUS.LOADING;
  const isInvalid =
    promoVerificationStatus === PROMO_CODE_VERIFICATION_STATUS.INVALID;
  const isError =
    promoVerificationStatus === PROMO_CODE_VERIFICATION_STATUS.ERROR;

  const widgetTexts = useMemo(() => {
    return {
      headerText: intl.formatMessage({ id: 'promotion.have_promo_code' }),
      placeholderText: intl.formatMessage({
        id: 'promotion.enter_promo_code_placeholder',
      }),
    };
  }, [intl]);

  useEffect(() => {
    if (promoCodeInputRef.current) {
      promoCodeInputRef.current.focus();
    }
  }, [showInputWidget]);

  useEffect(() => {
    //disable promo codes to prevent double click
    const appliedPromotions = appliedPromotionsData?.appliedPromotions || [];
    const shouldDisable =
      promoVerificationStatus === PROMO_CODE_VERIFICATION_STATUS.LOADING ||
      appliedPromotions.length > 0;

    setDisabledPromoCodes(shouldDisable);
  }, [promoVerificationStatus, appliedPromotionsData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabledPromoCodes) {
      e.preventDefault();
      return;
    }
    setPromoCode(e.target.value);
  };

  const handleCodeSubmit = () => {
    if (disabledPromoCodes) {
      return;
    }
    if (promoCode !== '') {
      onPromoCodeApply(promoCode);
    }
  };

  const removePromocode = () => {
    if (appliedPromoCode) {
      setPromoCode('');
      resetPromocode();
    }
  };

  const appliedPromoCodeText = useMemo(() => {
    const promoCode = availablePromoCodes.find(
      (code) => code.code === appliedPromoCode
    );
    return {
      code: promoCode?.code,
      name: promoCode?.name,
    };
  }, [appliedPromoCode, availablePromoCodes]);

  const handleToggleInputWidget = () => {
    if (disabledPromoCodes) {
      return;
    }
    setInputWidget(true);
  };

  // Early return if promo code is applied
  if (appliedPromoCode !== '') {
    return (
      <div
        className='promo-code-widget applied align-items-center'
        key={'applied-banner'}
      >
        <div className='applied-promocode d-flex align-items-center justify-content-between'>
          <div className='promo-info d-flex'>
            <div className='promo-icon mr-2'>
              <img src={promotionCoupon} alt='promotion coupon' />
            </div>
            <div className='d-flex flex-column'>
              <div className='promocode-name'>
                <span className='code'>{appliedPromoCodeText.code}</span>{' '}
                <FormattedMessage
                  id='promotion.coupon_applied'
                  defaultMessage='Coupon Applied - '
                />
              </div>
              <div className='promo-description'>
                {appliedPromoCodeText.name}
              </div>
            </div>
          </div>
          <div className='remove-promo-button' onClick={removePromocode}>
            <FormattedMessage id='promotion.remove' defaultMessage='Remove' />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='widget promo-code-widget' key={'promo-input'}>
      <label className='input-label bold mb-0'>{widgetTexts.headerText}</label>
      {showInputWidget ? (
        <div className='promo-input-area'>
          <input
            ref={promoCodeInputRef}
            type='text'
            value={promoCode}
            onChange={handleInputChange}
            placeholder={widgetTexts.placeholderText}
            disabled={isLoading || disabledPromoCodes}
            className='form-control promo-input'
          />
          <button
            className='btn px-4 ml-2 btn-primary'
            onClick={handleCodeSubmit}
            disabled={isLoading || promoCode === '' || disabledPromoCodes}
          >
            {isLoading ? (
              <FormattedMessage
                id='promotion.applying'
                defaultMessage='Applying...'
              />
            ) : (
              <FormattedMessage id='global.apply' defaultMessage='Apply' />
            )}
          </button>
        </div>
      ) : (
        <div className='promotion-input d-flex justify-content-between'>
          <div>{widgetTexts.headerText}</div>
          <div
            className={`toggle-link ${disabledPromoCodes ? 'disabled' : ''}`}
            onClick={handleToggleInputWidget}
          >
            <FormattedMessage
              id='promotion.enter_promo_code'
              defaultMessage='Enter Code'
            />
          </div>
        </div>
      )}

      {isInvalid && (
        <div className='error-message'>
          <FormattedMessage
            id='promotion.invalid_promo_code'
            defaultMessage='Promo Code Not Available'
          />
        </div>
      )}
      {isError && (
        <div className='error-message'>
          <FormattedMessage
            id='promotion.error_applying_promo_code'
            defaultMessage='Error applying promo code!'
          />
        </div>
      )}
    </div>
  );
};

export default PromoCodeWidget;
